import React, { useState } from 'react';
import { apiFetch } from '../services/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Lightbulb, Send, ThumbsUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Schulungswunsch-Formular. Kann auf jeder Seite eingebettet werden.
 * Wenn eingeloggt (Chef oder Mitarbeiter), werden Name/Email
 * automatisch übernommen — wir zeigen die Felder nur bei anonymen
 * Besuchern.
 */
export const TrainingWishForm = ({
  variant = 'card',              // 'card' | 'inline'
  isAuthenticated = false,
  className = '',
}) => {
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [thanksMode, setThanksMode] = useState(false);
  const [upvoted, setUpvoted] = useState(false);

  const submit = async () => {
    const t = text.trim();
    if (t.length < 5) return toast.error('Bitte mindestens 5 Zeichen eingeben');
    if (t.length > 1000) return toast.error('Maximal 1000 Zeichen');
    setSending(true);
    try {
      const body = { text: t };
      if (!isAuthenticated) {
        if (name.trim()) body.requester_name = name.trim();
        if (email.trim()) body.requester_email = email.trim();
      }
      const res = await apiFetch(`${API}/api/training-wishes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Konnte Wunsch nicht speichern');
      setUpvoted(!!data.upvoted);
      setThanksMode(true);
      setText('');
    } catch (err) {
      toast.error(err.message || 'Fehler');
    } finally {
      setSending(false);
    }
  };

  // WICHTIG: Keine inline-definierte `Wrapper`-Komponente mehr!
  // Vorher wurde pro Render eine neue Function-Reference erzeugt — React behandelte
  // sie als neuen Komponenten-Typ und mountete den gesamten Teilbaum neu, wodurch
  // die Textarea nach jedem Tastendruck den Fokus verlor. Jetzt rendern wir den
  // Rahmen über eine Memo-stabile Hülle direkt im JSX.
  const content = thanksMode ? (
    <div className="text-center py-3" data-testid="wish-thanks">
      {upvoted ? (
        <>
          <ThumbsUp className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
          <p className="text-white font-semibold">Danke – wir haben deinen Vote registriert.</p>
          <p className="text-sm text-gray-400 mt-1">
            Genau dieser Wunsch wurde schon eingereicht. Je mehr Nachfrage, desto schneller landet er in der Roadmap.
          </p>
        </>
      ) : (
        <>
          <Lightbulb className="w-8 h-8 mx-auto mb-2 text-yellow-400" />
          <p className="text-white font-semibold">Wunsch ist unterwegs.</p>
          <p className="text-sm text-gray-400 mt-1">
            Vielen Dank! Wir werten euer Feedback wöchentlich aus und priorisieren die häufigsten Themen zuerst.
          </p>
        </>
      )}
      <Button
        variant="outline"
        size="sm"
        className="mt-4 border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10"
        onClick={() => { setThanksMode(false); setUpvoted(false); }}
        data-testid="wish-new-btn"
      >
        Weiteren Wunsch einreichen
      </Button>
    </div>
  ) : (
    <>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-400 shrink-0">
          <Lightbulb className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-white font-semibold text-base leading-tight">
            Schulung vermisst? Thema wünschen!
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Sag uns, welchen Kurs du dir wünschst — wir bauen die meistgefragten Themen zuerst.
          </p>
        </div>
      </div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="z. B. „Wir brauchen dringend eine kurze Auffrischung zur DIN VDE 0100-712 für PV-Baustellen mit LG-Speichern."
        className="bg-black/40 border-gray-700 text-white placeholder:text-gray-500 min-h-[100px]"
        data-testid="wish-text-input"
      />
      {!isAuthenticated && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name (optional)"
            className="bg-black/40 border-gray-700 text-white placeholder:text-gray-500"
            data-testid="wish-name-input"
          />
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="E-Mail (optional, für Rückfragen)"
            className="bg-black/40 border-gray-700 text-white placeholder:text-gray-500"
            data-testid="wish-email-input"
          />
        </div>
      )}
      <div className="flex justify-end mt-3">
        <Button
          onClick={submit}
          disabled={sending || text.trim().length < 5}
          className="bg-yellow-500 hover:bg-yellow-400 text-black font-semibold"
          data-testid="wish-submit-btn"
        >
          {sending ? (
            <><div className="animate-spin h-4 w-4 border-2 border-black border-t-transparent rounded-full mr-2" /> Sende…</>
          ) : (
            <><Send className="w-4 h-4 mr-2" /> Wunsch einreichen</>
          )}
        </Button>
      </div>
    </>
  );

  if (variant === 'inline') {
    return <div className={className}>{content}</div>;
  }
  return (
    <Card className={`bg-gradient-to-br from-yellow-500/10 via-amber-500/5 to-transparent border-yellow-500/30 ${className}`}>
      <CardContent className="p-5">{content}</CardContent>
    </Card>
  );
};

export default TrainingWishForm;
